import React, { useState, useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Helmet } from "react-helmet"
import NavigationBar from "../../components/ui/NavigationBar"
import Input from "../../components/ui/Input"
import Select from "../../components/ui/Select"
import Button from "../../components/ui/Button"
import Icon from "../../components/AppIcon"
import ModuleEditor from "./components/ModuleEditor"
import { readImageAsBase64 } from "../../utils/imageHelpers"
import QuizEditor from "./components/QuizEditor"
import CapstoneEditor from "./components/CapstoneEditor"
import LearnersTab from "./components/LearnersTab"
import LiveClassesTab from "./components/LiveClassesTab"
import CertificatesTab from "./components/CertificatesTab"
import CourseAnalyticsTab from "./components/CourseAnalyticsTab"
import ComingSoonPanel from "../organization-dashboard/components/ComingSoonPanel"
import ReauthModal from "../../components/ui/ReauthModal"
import {
  getCourse,
  updateCourse,
  updateCourseModules,
  publishCourse,
  unpublishCourse,
  generateLocalId,
} from "../../utils/courseHelpers"
import { createEmptyModule, COURSE_CATEGORIES } from "../../utils/lessonTypes"
import { syncCourseToCatalog, removeCourseFromCatalog } from "../../utils/catalogHelpers"
import { getOrganization } from "../../utils/orgHelpers"

const ENROLLMENT_OPTIONS = [
  { value: "invite", label: "Invite Only" },
  { value: "open", label: "Open Enrollment" },
  { value: "csv", label: "Bulk CSV Upload" },
  { value: "cohort", label: "Cohort" },
]

const TABS = [
  { key: "overview", label: "Overview", icon: "LayoutDashboard" },
  { key: "curriculum", label: "Curriculum", icon: "Layers" },
  { key: "live-classes", label: "Live Classes", icon: "Video" },
  { key: "assessments", label: "Assessments", icon: "ClipboardCheck" },
  { key: "learners", label: "Learners", icon: "Users" },
  { key: "certificates", label: "Certificates", icon: "Award" },
  { key: "analytics", label: "Analytics", icon: "BarChart3" },
  { key: "projects", label: "Projects", icon: "FolderKanban" },
  { key: "discussions", label: "Discussions", icon: "MessageSquare" },
  { key: "settings", label: "Settings", icon: "Settings" },
  { key: "publishing", label: "Publishing", icon: "Rocket" },
]

const OrganizationCourseBuilder = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const orgId = searchParams?.get("orgId")
  const courseId = searchParams?.get("courseId")

  const [activeTab, setActiveTab] = useState("overview")
  const [course, setCourse] = useState(null)
  const [modules, setModules] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState("")
  const [reauthAction, setReauthAction] = useState(null) // "publish" | "visibility" | null
  const [pendingVisibilityValue, setPendingVisibilityValue] = useState(null)
  const [thumbnailError, setThumbnailError] = useState("")
  const [publishError, setPublishError] = useState("")

  useEffect(() => {
    const load = async () => {
      if (!orgId || !courseId) {
        navigate("/organization-dashboard", { replace: true })
        return
      }

      const { course: courseData, error } = await getCourse(orgId, courseId)

      if (error || !courseData) {
        navigate("/organization-dashboard", { replace: true })
        return
      }

      setCourse(courseData)
      setModules(courseData?.modules || [])
      setLoading(false)
    }

    load()
  }, [orgId, courseId, navigate])

  const handleCourseFieldChange = (field) => (e) => {
    setCourse((prev) => ({ ...prev, [field]: e?.target?.value }))
  }

  const handleEnrollmentChange = (value) => {
    setCourse((prev) => ({ ...prev, enrollmentType: value }))
  }

  const handleThumbnailUpload = async (e) => {
    const file = e?.target?.files?.[0]
    if (!file) return

    setThumbnailError("")
    const { base64, error } = await readImageAsBase64(file, 500 * 1024)

    if (error) {
      setThumbnailError(error?.message)
      return
    }

    setCourse((prev) => ({ ...prev, thumbnailUrl: base64 }))
  }

  const addModule = () => {
    setModules((prev) => [...prev, createEmptyModule(generateLocalId())])
  }

  const updateModule = (index, updatedModule) => {
    setModules((prev) => prev?.map((m, i) => (i === index ? updatedModule : m)))
  }

  const deleteModule = (index) => {
    if (!window.confirm("Delete this module and all its lessons?")) return
    setModules((prev) => prev?.filter((_, i) => i !== index))
  }

  const moveModule = (index, direction) => {
    setModules((prev) => {
      const newIndex = index + direction
      if (newIndex < 0 || newIndex >= prev?.length) return prev
      const updated = [...prev]
      ;[updated[index], updated[newIndex]] = [updated[newIndex], updated[index]]
      return updated
    })
  }

    const handleSaveAll = async () => {
    setSaving(true)
    setSaveMessage("")

    const orderedModules = modules?.map((m, i) => ({
      ...m,
      order: i,
      lessons: m?.lessons?.map((l, li) => ({ ...l, order: li })),
    }))

    const { error: courseError } = await updateCourse(orgId, courseId, {
      title: course?.title,
      description: course?.description,
      enrollmentType: course?.enrollmentType,
      category: course?.category,
      capstone: course?.capstone || null,
      thumbnailUrl: course?.thumbnailUrl || "",
    })
    const { error: modulesError } = await updateCourseModules(orgId, courseId, orderedModules)

    if (!courseError && !modulesError && course?.status === "published" && course?.visibility === "public") {
      const { organization } = await getOrganization(orgId)
      await syncCourseToCatalog({
        orgId,
        orgName: organization?.name,
        orgVerified: organization?.verified,
        orgNesolearnId: orgId,
        course: { ...course, courseId, modules: orderedModules },
      })
    }

    setSaving(false)

    if (courseError || modulesError) {
      setSaveMessage("Something went wrong saving. Please try again.")
      return
    }

    setModules(orderedModules)
    setSaveMessage("Saved successfully.")
    setTimeout(() => setSaveMessage(""), 3000)
  }

  const handleTogglePublish = () => {
    if (course?.status === "published") {
      doUnpublish()
      return
    }
    if (!course?.thumbnailUrl) {
      setPublishError("Add a cover image before publishing — courses need one to look right in the catalog.")
      return
    }
    setPublishError("")
    setReauthAction("publish")
  }

  const doUnpublish = async () => {
    await unpublishCourse(orgId, courseId)
    await removeCourseFromCatalog(courseId)
    setCourse((prev) => ({ ...prev, status: "draft" }))
  }

  const doPublish = async () => {
    await publishCourse(orgId, courseId)
    const updatedCourse = { ...course, status: "published" }
    setCourse(updatedCourse)

    if (course?.visibility === "public") {
      const { organization } = await getOrganization(orgId)
      await syncCourseToCatalog({
        orgId,
        orgName: organization?.name,
        orgVerified: organization?.verified,
        orgNesolearnId: orgId,
        course: { ...updatedCourse, courseId, modules },
      })
    }
  }

  const handleVisibilityChange = (value) => {
    if (value === "public") {
      setPendingVisibilityValue(value)
      setReauthAction("visibility")
      return
    }
    doVisibilityChange(value)
  }

  const doVisibilityChange = async (value) => {
    setCourse((prev) => ({ ...prev, visibility: value }))
    await updateCourse(orgId, courseId, { visibility: value })

    if (course?.status !== "published") return

    if (value === "public") {
      const { organization } = await getOrganization(orgId)
      await syncCourseToCatalog({
        orgId,
        orgName: organization?.name,
        orgVerified: organization?.verified,
        orgNesolearnId: orgId,
        course: { ...course, courseId, visibility: value, modules },
      })
    } else {
      await removeCourseFromCatalog(courseId)
    }
  }

  const handleReauthConfirm = async () => {
    if (reauthAction === "publish") {
      await doPublish()
    } else if (reauthAction === "visibility") {
      await doVisibilityChange(pendingVisibilityValue)
    }
    setReauthAction(null)
    setPendingVisibilityValue(null)
  }

  const handleReauthClose = () => {
    setReauthAction(null)
    setPendingVisibilityValue(null)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="text-muted-foreground">Loading course...</span>
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>{course?.title || "Course Builder"} - Nesolearn</title>
      </Helmet>

      <div className="min-h-screen bg-background">
        <NavigationBar />

        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between mb-6">
            <Button
              variant="ghost"
              iconName="ArrowLeft"
              iconPosition="left"
              onClick={() => navigate("/organization-dashboard")}
            >
              Back to Courses
            </Button>
            {saveMessage && (
              <span className="text-sm text-success flex items-center gap-1">
                <Icon name="CheckCircle2" size={14} />
                {saveMessage}
              </span>
            )}
          </div>

          <div className="mb-6">
            <h1 className="text-2xl font-bold text-foreground">{course?.title || "Untitled Course"}</h1>
          </div>

          <div className="border-b border-border mb-6 overflow-x-auto">
            <div className="flex gap-1 min-w-max">
              {TABS?.map((tab) => (
                <button
                  key={tab?.key}
                  type="button"
                  onClick={() => setActiveTab(tab?.key)}
                  className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab?.key
                      ? "border-accent text-accent"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Icon name={tab?.icon} size={14} />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>

          {activeTab === "overview" && (
            <div className="bg-card border border-border rounded-xl p-6 space-y-4">
              <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">Course Details</h2>
              <p className="text-xs text-muted-foreground -mt-2">
                Fields marked <span className="text-destructive">*</span> are required to publish.
              </p>
              <Input label="Title" value={course?.title} onChange={handleCourseFieldChange("title")} />

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground block">
                  Cover Image <span className="text-destructive">*</span>
                </label>
                {course?.thumbnailUrl && (
                  <img
                    src={course?.thumbnailUrl}
                    alt="Cover preview"
                    className="w-full h-40 object-cover rounded-lg border border-border mb-2"
                  />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailUpload}
                  className="text-sm text-muted-foreground file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-muted file:text-foreground file:text-sm hover:file:bg-muted/80"
                />
                <p className="text-xs text-muted-foreground">
                  Max 500KB. Required before publishing — this shows on the catalog, course preview, and course cards.
                </p>
                {thumbnailError && <p className="text-sm text-destructive">{thumbnailError}</p>}
              </div>
              <div>
                <label className="text-sm font-medium text-[#f0f0f0] mb-2 block">Description</label>
                <textarea
                  rows={3}
                  value={course?.description}
                  onChange={handleCourseFieldChange("description")}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            </div>
          )}

          {activeTab === "curriculum" && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-foreground">Curriculum</h2>
                <Button variant="outline" iconName="Plus" iconPosition="left" onClick={addModule}>
                  Add Module
                </Button>
              </div>

              <div className="space-y-4">
                {modules?.length === 0 && (
                  <div className="bg-card border border-border rounded-xl p-12 text-center">
                    <Icon name="Layers" size={28} className="text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground text-sm">No modules yet. Add your first module to get started.</p>
                  </div>
                )}

                {modules?.map((module, index) => (
                  <div key={module?.id} className="space-y-2">
                    <ModuleEditor
                      module={module}
                      index={index}
                      onChange={(updated) => updateModule(index, updated)}
                      onDelete={() => deleteModule(index)}
                      onMoveUp={() => moveModule(index, -1)}
                      onMoveDown={() => moveModule(index, 1)}
                    />
                    <QuizEditor
                      quiz={module?.quiz}
                      onChange={(updatedQuiz) => updateModule(index, { ...module, quiz: updatedQuiz })}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

            {activeTab === "live-classes" && <LiveClassesTab orgId={orgId} courseId={courseId} />}

          {activeTab === "assessments" && (
            <CapstoneEditor
              capstone={course?.capstone}
              certificationEnabled={course?.certification?.enabled}
              onChange={(updatedCapstone) => setCourse((prev) => ({ ...prev, capstone: updatedCapstone }))}
            />
          )}

          {activeTab === "learners" && <LearnersTab orgId={orgId} courseId={courseId} />}

          {activeTab === "certificates" && <CertificatesTab orgId={orgId} courseId={courseId} />}

          {activeTab === "analytics" && <CourseAnalyticsTab orgId={orgId} course={course} />}

          {activeTab === "projects" && <ComingSoonPanel label="Projects" />}

          {activeTab === "discussions" && <ComingSoonPanel label="Discussions" />}

          {activeTab === "settings" && (
            <div className="bg-card border border-border rounded-xl p-6 space-y-4">
              <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">Settings</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-[#f0f0f0] mb-2 block">Enrollment Type</label>
                  <Select options={ENROLLMENT_OPTIONS} value={course?.enrollmentType} onChange={handleEnrollmentChange} />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#f0f0f0] mb-2 block">Category</label>
                  <Select
                    options={COURSE_CATEGORIES?.map((c) => ({ value: c, label: c }))}
                    value={course?.category || "Other"}
                    onChange={(value) => setCourse((prev) => ({ ...prev, category: value }))}
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === "publishing" && (
            <div className="bg-card border border-border rounded-xl p-6 space-y-4">
              <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">Publishing</h2>

              <div>
                <label className="text-sm font-medium text-[#f0f0f0] mb-2 block">Visibility</label>
                <Select
                  options={[
                    { value: "private", label: "Private (org only)" },
                    { value: "public", label: "Public (Nesolearn catalog)" },
                  ]}
                  value={course?.visibility || "private"}
                  onChange={handleVisibilityChange}
                />
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-border">
                <div>
                  <p className="text-sm font-medium text-foreground">Status</p>
                  <p className="text-xs text-muted-foreground">
                    {course?.status === "published" ? "This course is live." : "This course is a draft."}
                  </p>
                </div>
                {publishError && <p className="text-xs text-destructive">{publishError}</p>}
                <Button variant={course?.status === "published" ? "outline" : "success"} onClick={handleTogglePublish}>
                  {course?.status === "published" ? "Unpublish" : "Publish Course"}
                </Button>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
            <Button variant="outline" iconName="ArrowLeft" iconPosition="left" onClick={() => navigate("/organization-dashboard")}>
              Back to Courses
            </Button>

            <div className="flex items-center gap-3">
              <Button variant="default" loading={saving} onClick={handleSaveAll}>
                Save All Changes
              </Button>
              <Button variant="secondary" iconName="ArrowRight" iconPosition="right" onClick={() => navigate("/organization-dashboard")}>
                Done
              </Button>
            </div>
          </div>
        </main>
      </div>

      <ReauthModal
        open={!!reauthAction}
        label={reauthAction === "publish" ? "publish this course" : "make this course public"}
        onConfirm={handleReauthConfirm}
        onClose={handleReauthClose}
      />
    </>
  )
}

export default OrganizationCourseBuilder